import MapsUgcOutlinedIcon from '@mui/icons-material/MapsUgcOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import { TextareaAutosize } from '@mui/material';
import React, {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import './QATextInputView.scss';
import { useHistory, useLocation } from 'react-router-dom';
import { BooleanParam, StringParam, useQueryParams } from 'use-query-params';
import { AppName } from '../../apps/definition';
import { AppState } from '../../constants';
import { trackEvent } from '../../extra/sharedMethods';
import { PromptModalTypes } from '../../models/Prompts';
import { QAStages } from '../../models/QAmodels';
import { useAppConnectionStatus } from '../../pages/onboarding/WorkspaceSetupPage/hooks/app-connection-summary';
import {
  QAControllerEventArgs,
  useQAController,
} from '../../scripts/QAController';
import { AnalyticsEvent } from '../../scripts/constants/analytics-event';
import {
  QueryParams,
  useFlag,
  useToaster,
  useUserSafe,
} from '../../scripts/hooks';
import { useRecommendedPrompts } from '../../scripts/hooks/prompts';
import { logError } from '../../scripts/utils';
import { AppsFilterSwitch } from '../AppsFilterSwitch/AppsFilterSwitch';
import { UIIcon } from '../controls/ui/UIIcon/UIIcon';
import { UIIconButton } from '../controls/ui/UIIconButton/UIIconButton';
import { UITooltip } from '../controls/ui/UIToolTip/UIToolTip';
import { RecommendedWorkflows } from '../recommendedWorkflows/RecommendedWorkflows';

export interface SendQueryArgs {
  queryText: string;
  conversation_id?: string;
  filter_skills: FilterSkills[];
  isBaseLLM: boolean;
}

export interface ConnectedApps {
  displayName: string;
  appName: AppName;
  id?: number;
  workspaceName?: string;
  connectionName?: string;
}

export interface FilterSkills {
  app_name: AppName;
  app_id: number;
}

interface RouteState {
  template?: string;
  searchFilters?: AppName[];
  baseLlm?: boolean;
}

// eslint-disable-next-line max-lines-per-function
export const QATextInputView: FC = () => {
  const stopGenerationFlag = useFlag('stopAnswerGeneration');
  const isWorkflowsEnabled = useFlag('workflowsEnabled');
  const multiAppsEnabled = useFlag('multiAppsEnabled');
  const aiControlsEnabled = useFlag('aiControlsEnabled');
  const connectedApps = useAppConnectionStatus();
  const history = useHistory();
  const location = useLocation<RouteState>();

  const sortedInstantConnectedApps = useMemo(() => {
    const instantConnectedApps = connectedApps.reduce(
      (acc: ConnectedApps[], curr) => {
        if (
          curr.showInAppStore &&
          curr.isInstant &&
          curr.statusCode !== AppState.ErrorReconnect
        ) {
          acc.push({
            appName: curr.appName,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            displayName: curr.displayName,
            id: curr.id,
            connectionName: curr.connectionName,
            workspaceName: curr.workspaceName,
          });
        } else if (
          curr.showInAppStore &&
          !curr.isInstant &&
          curr.statusCode === AppState.IndexingComplete
        ) {
          acc.push({
            appName: curr.appName,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            displayName: curr.displayName,
            id: curr.id,
            workspaceName: curr.workspaceName,
          });
        }

        return acc;
      },
      []
    );

    // If multiAppsEnabled is true, we don't need to filter out duplicates
    if (multiAppsEnabled) {
      return instantConnectedApps.sort((appA, appB) => {
        return appA.appName.localeCompare(appB.appName);
      });
    }

    const uniqueConnectedApps = instantConnectedApps.filter(
      (app, index, self) =>
        index === self.findIndex((t) => t.appName === app.appName)
    );

    uniqueConnectedApps.sort((appA, appB) => {
      return appA.appName.localeCompare(appB.appName);
    });

    return uniqueConnectedApps;
  }, [connectedApps, multiAppsEnabled]);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchFilters, setSearchFilters] = useState<ConnectedApps[]>(
    sortedInstantConnectedApps
  );

  const [dynamicPlaceholder, setDynamicPlaceholder] = useState('');

  const [isBaseLLM, setIsBaseLLM] = useState(false);

  const previousSortedAppsRef = useRef(sortedInstantConnectedApps);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const qaController = useQAController();

  const isLoaded = useRef(false);
  const [queryParams] = useQueryParams({
    [QueryParams.IsBaseLLM]: BooleanParam,
    [QueryParams.IsWeb]: BooleanParam,
    [QueryParams.Search]: StringParam,
  });

  const { prompts: recommendedPrompts } = useRecommendedPrompts();

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (location.state?.template) {
      setSearchQuery(location.state.template);
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (location.state?.baseLlm) {
      setIsBaseLLM(true);
      setSearchFilters([]);
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    } else if (location.state?.searchFilters) {
      if (location.state.searchFilters[0] === 'web') {
        setSearchFilters([{ id: 0, appName: 'web', displayName: 'Web' }]);
      } else {
        setSearchFilters(
          previousSortedAppsRef.current.filter((app) =>
            location.state.searchFilters!.includes(app.appName)
          )
        );
      }

      history.replace({ ...location, state: {} });
    }
  }, [history, location]);

  const { currentStage } = qaController.useProgressStage();

  const toaster = useToaster();
  const userEmail = useUserSafe((u) => u.email);
  const orgByOrg = useUserSafe((u) => u.orgByOrg);

  const { isNewTopic } = qaController.getIsNewTopic();

  const messages = qaController.useMessages();

  const conversation_id = messages[messages.length - 1]?.conversation_id;

  const isOnboardingQuestion = useMemo(
    () =>
      messages[messages.length - 1]?.extraData?.staticAnswerType ===
        // eslint-disable-next-line @cspell/spellchecker
        'ONBOARDING_INTIAL' ||
      messages[messages.length - 1]?.extraData?.staticAnswerType ===
        'ONBOARDING_APPS_CONNECTED',
    [messages]
  );

  const placeholderText = useMemo(() => {
    if (isBaseLLM) {
      return 'Ask AI without searching your Apps or Web...';
    }

    if (searchFilters.length === 1 && searchFilters[0]?.appName === 'web') {
      return 'Ask to find answers from the Web...';
    }

    return 'Ask to find answers from your Apps...';
  }, [isBaseLLM, searchFilters]);

  useEffect(() => {
    setDynamicPlaceholder('');
    const words = placeholderText.split(' ');
    let currentText = '';
    let index = 0;
    let intervalTime = 90;

    const addWords = () => {
      if (index < words.length) {
        currentText += words[index];
        index++;

        currentText += ' ';
        setDynamicPlaceholder(currentText);
        intervalTime -= 10;
        setTimeout(addWords, intervalTime);
      }
    };

    const timeoutId = setTimeout(addWords, intervalTime);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [placeholderText]);

  const sendQuery = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types
    (query: string, isWeb = false, isBaseChat: boolean = false) => {
      if (query === '') {
        return;
      }

      if (currentStage !== QAStages.WAIT_USER_INPUT) {
        trackEvent(AnalyticsEvent.QAUserInputWhilePreviousResponseLoading);
        toaster.failure(
          'Please hold on while we generate an answer for your previous query.'
        );

        return;
      }

      trackEvent(
        AnalyticsEvent.QAUserAskedQuestion,
        {
          user: userEmail,
          timestamp: new Date(),
          searchFilters,
        },
        {
          question: query,
        }
      );

      let filter_skills: FilterSkills[] = [];

      if (isWeb) {
        // if isWeb from query params, filter_skills app_name should be 'web'
        filter_skills = [{ app_id: 0, app_name: 'web' }];
      } else if (isBaseChat || isBaseLLM) {
        // if isBaseLLM from query params, filter_skills should be empty
        filter_skills = [];
      } else {
        // if neither isWeb nor isBaseLLM, filter_skills should be the list of apps
        filter_skills = searchFilters.reduce((acc: FilterSkills[], curr) => {
          acc.push({ app_id: curr.id!, app_name: curr.appName });
          return acc;
        }, []);
      }

      const args: SendQueryArgs = {
        queryText: query,
        filter_skills,
        isBaseLLM: isBaseChat || isBaseLLM,
      };

      if (!isNewTopic) {
        args.conversation_id = conversation_id;
      }

      qaController.sendNewMessage(args).catch((error) => {
        logError(error);
        toaster.failure('Error processing request. Please try again.');
      });

      qaController.triggerEvent('newTopic', { newTopic: false });

      qaController.setIsNewTopic(false);
    },
    [
      conversation_id,
      currentStage,
      isBaseLLM,
      isNewTopic,
      qaController,
      userEmail,
      searchFilters,
      toaster,
    ]
  );

  useEffect(() => {
    if (queryParams[QueryParams.Search] && !isLoaded.current) {
      sendQuery(
        queryParams[QueryParams.Search],
        !!queryParams[QueryParams.IsWeb],
        !!queryParams[QueryParams.IsBaseLLM]
      );

      if (queryParams[QueryParams.IsWeb]) {
        setSearchFilters([{ appName: 'web', displayName: 'Web', id: 0 }]);
      }

      if (queryParams[QueryParams.IsBaseLLM]) {
        setIsBaseLLM(true);
        setSearchFilters([]);
      }

      window.history.replaceState({}, document.title, window.location.pathname);

      isLoaded.current = true;
    }
  }, [queryParams, sendQuery, isLoaded]);

  useEffect(() => {
    if (
      previousSortedAppsRef.current.length !== sortedInstantConnectedApps.length
    ) {
      setSearchFilters(sortedInstantConnectedApps);
    }

    // Update the ref after making the comparison
    previousSortedAppsRef.current = sortedInstantConnectedApps;
  }, [sortedInstantConnectedApps]);

  const sendSearchQuery = useCallback(() => {
    let isWeb = false;
    if (searchFilters[0]?.appName === 'web') {
      isWeb = true;
    }

    sendQuery(searchQuery, isWeb, isBaseLLM);
    if (currentStage === QAStages.WAIT_USER_INPUT) {
      setSearchQuery('');
    }
  }, [currentStage, isBaseLLM, searchFilters, searchQuery, sendQuery]);

  qaController.listenEvent(
    'setQueryAndFilter',
    ({ query, queryFilters, baseLlm }) => {
      if (
        query !== undefined &&
        queryFilters !== undefined &&
        baseLlm !== undefined
      ) {
        setSearchQuery(query);
        setSearchFilters(queryFilters);
        setIsBaseLLM(baseLlm);
        inputRef.current?.focus();
      }
    }
  );

  // TODO: Move the setSearchQuery logic to UITextArea
  const onKeyDown = useCallback(
    (evt: React.KeyboardEvent) => {
      if (evt.key === 'Enter') {
        evt.preventDefault();
        if (evt.ctrlKey || evt.metaKey || evt.shiftKey || evt.altKey) {
          setSearchQuery(`${searchQuery}\n`);
        } else {
          sendSearchQuery();
        }
      }
    },
    [searchQuery, sendSearchQuery]
  );

  const handleSavePromptTemplate = useCallback(
    ({ promptTemplate }: Partial<QAControllerEventArgs>) => {
      if (promptTemplate) {
        const searchFiltersStrings = searchFilters.map((app) => app.appName);

        trackEvent(AnalyticsEvent.ClickedOnSaveWorkflowButton, {
          searchFilters: searchFiltersStrings,
          promptTemplate,
        });

        qaController.triggerEvent('showPromptModal', {
          promptModalType: PromptModalTypes.CREATE,
          promptTemplate,
          searchFilters: searchFiltersStrings,
        });
      }
    },
    [qaController, searchFilters]
  );

  useEffect(() => {
    qaController.listenEvent('savePrompt', handleSavePromptTemplate);
    return () => {
      qaController.off('savePrompt', handleSavePromptTemplate);
    };
  }, [handleSavePromptTemplate, qaController]);

  const handleNewTopicButton = useCallback(() => {
    qaController.setIsNewTopic(!isNewTopic);
    trackEvent(AnalyticsEvent.QANewTopicToggled, { user: userEmail });
  }, [isNewTopic, userEmail, qaController]);

  const handleViewTemplatesButton = useCallback(() => {
    trackEvent(AnalyticsEvent.OpenWorkflowsModalFromDashAi, {});

    qaController.triggerEvent('showPromptModal', {
      promptModalType: PromptModalTypes.VIEW_ALL_TEMPLATES,
    });
  }, [qaController]);

  const handleTextInputChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      // fix for the bug where the text area has new line
      if (searchQuery === '' && event.target.value === '\n') {
        setSearchQuery('');
      } else {
        setSearchQuery(event.target.value);
      }
    },
    [searchQuery, setSearchQuery]
  );

  const handleStopGeneration = useCallback(() => {
    qaController.triggerEvent('stopGenerating', {});
  }, [qaController]);

  const showRecommendedWorkflows = useMemo(() => {
    return isWorkflowsEnabled && !isOnboardingQuestion && isNewTopic;
  }, [isWorkflowsEnabled, isNewTopic, isOnboardingQuestion]);

  return (
    <div className="qaTextInputWrapper w-screen sm:w-full px-1 sm:px-0">
      {showRecommendedWorkflows && (
        <div className="w-full max-w-[750px] mb-2">
          <RecommendedWorkflows recommendedWorkflows={recommendedPrompts} />
        </div>
      )}
      <div className="flex flex-col gap-4 w-full px-1 max-w-[750px] min-h-[90px] border border-solid border-gray-30 rounded-lg pb-3 shadow-flat">
        <div className="flex px-3 pt-3">
          <TextareaAutosize
            autoFocus
            className="bg-transparent block text-base flex-1 border-0 rounded-lg resize-none outline-none"
            maxRows={8}
            onChange={handleTextInputChange}
            onKeyDown={onKeyDown}
            placeholder={dynamicPlaceholder}
            ref={inputRef}
            value={searchQuery}
          />
        </div>
        <div className="flex justify-between items-center px-3">
          <div className="flex items-center gap-2">
            <div className="flex">
              <UITooltip
                title={isNewTopic ? 'Resume previous topic' : 'Start new topic'}
              >
                <div
                  className="hover:bg-cloud-10 p-2 rounded-md cursor-pointer"
                  onClick={handleNewTopicButton}
                >
                  {isNewTopic ? (
                    <ReplayOutlinedIcon
                      sx={{ display: 'block', fontSize: 16 }}
                    />
                  ) : (
                    <MapsUgcOutlinedIcon
                      sx={{ display: 'block', fontSize: 16 }}
                    />
                  )}
                </div>
              </UITooltip>
              {isWorkflowsEnabled && (
                <UITooltip title="View workflows">
                  <div
                    className="hover:bg-cloud-10 p-2 rounded-md cursor-pointer"
                    onClick={handleViewTemplatesButton}
                  >
                    <UIIcon name="bolt" style={{ display: 'block' }} />
                  </div>
                </UITooltip>
              )}
            </div>
            <AppsFilterSwitch
              isBaseLLM={isBaseLLM}
              searchFilters={searchFilters}
              setIsBaseLLM={setIsBaseLLM}
              setSearchFilters={setSearchFilters}
              sortedInstantConnectedApps={sortedInstantConnectedApps}
            />
          </div>
          <div>
            {currentStage !== QAStages.WAIT_USER_INPUT && stopGenerationFlag ? (
              <UIIconButton
                name="stop-circle"
                onClick={handleStopGeneration}
                size={24}
                tooltip="Stop generating answer"
                type="ui"
              />
            ) : (
              <UIIconButton name="send" onClick={sendSearchQuery} type="ui" />
            )}
          </div>
        </div>
      </div>

      {aiControlsEnabled && (
        <div className="mt-2 max-w-[750px] text-center text-xs text-cloud-40">
          {orgByOrg.disclaimer}
        </div>
      )}
    </div>
  );
};
